import { parseSubscribeAck } from "./chatEvents";

const SUBSCRIBE_RETRY_MS = 3_000;
const SUBSCRIBE_MAX_ATTEMPTS = 5;

/**
 * The only part of socket.io's `Socket` this needs.
 *
 * Narrowed deliberately: it keeps the state machine testable in the
 * plain-node unit project against a fake, with no DOM, no real socket.io
 * client, and no new test dependencies.
 */
interface SubscribeCapableSocket {
  emit(
    event: "subscribe",
    payload: { conversation_id: string },
    ack: (raw: unknown) => void,
  ): void;
}

interface SubscribeRetryOptions {
  /**
   * Called once per SUCCESSFUL subscribe ack — including the one that ends a
   * retry chain, and again on every later resubscribe (a reconnect resubscribes
   * every room, and each of those needs its own REST catch-up).
   */
  onSubscribed?: (conversationId: string) => void;
}

export interface SubscribeRetryController {
  /**
   * Subscribe to a room, retrying a `rate_limited` refusal on a bounded
   * schedule. Safe to call again for a room that already has an attempt in
   * flight — the previous attempt is superseded, never duplicated.
   */
  subscribe(conversationId: string): void;
  /** Drop one room's tracked attempt and cancel any armed retry. */
  cancel(conversationId: string): void;
  /** Cancel every room. Call from effect cleanup. */
  cancelAll(): void;
}

/**
 * Subscribe-with-retry for chat rooms, shared by `useConversationSocket` (one
 * room) and `useInboxSocket` (many).
 *
 * Staleness is IDENTITY-based, not a mutable `cancelled` flag: every
 * `subscribe()` call builds its OWN state object and overwrites the map entry,
 * so an ack whose object is no longer the map's current one is stale and a
 * no-op. Both hooks previously hand-rolled this with a single shared, mutated
 * object per room, and both got it wrong in the same way — an older ack could
 * resurrect itself and assign `state.timer`, clobbering a newer call's
 * already-armed timer and orphaning it to fire an extra subscribe later. That
 * orphan is what produced duplicate subscribes and up to double the attempt
 * cap against a gateway that was already rate-limiting.
 *
 * `attempt` is threaded per call rather than held as one counter, so the budget
 * belongs to a retry CHAIN. A page-lifetime counter is the other half of the
 * same bug: once spent, no later reconnect could ever retry again.
 */
export function createSubscribeRetry(
  socket: SubscribeCapableSocket,
  { onSubscribed }: SubscribeRetryOptions = {},
): SubscribeRetryController {
  const states = new Map<
    string,
    { timer: ReturnType<typeof setTimeout> | null }
  >();

  const subscribe = (conversationId: string, attempt = 1) => {
    // Supersede whatever was in flight or armed for this room.
    const previous = states.get(conversationId);
    if (previous?.timer) clearTimeout(previous.timer);

    const state: { timer: ReturnType<typeof setTimeout> | null } = {
      timer: null,
    };
    states.set(conversationId, state);

    socket.emit(
      "subscribe",
      { conversation_id: conversationId },
      (raw: unknown) => {
        // Stale if a newer subscribe() call for this room — a retry, a
        // reconnect's resubscribe, or a second concurrent attempt — has since
        // replaced this state, or `cancel` removed it.
        if (states.get(conversationId) !== state) return;

        const ack = parseSubscribeAck(raw);

        // `null` (unparseable) is treated exactly like a settled error: an ack
        // we can't read isn't one a retry would fix.
        if (!ack) return;

        if (ack.ok) {
          onSubscribed?.(conversationId);
          return;
        }

        if (ack.error === "rate_limited" && attempt < SUBSCRIBE_MAX_ATTEMPTS) {
          state.timer = setTimeout(() => {
            state.timer = null;
            subscribe(conversationId, attempt + 1);
          }, SUBSCRIBE_RETRY_MS);
          return;
        }

        // `not_found` / `invalid_payload` are settled — no retry changes the
        // answer. That room stays REST-only for this session.
      },
    );
  };

  const cancel = (conversationId: string) => {
    const state = states.get(conversationId);
    if (!state) return;
    if (state.timer) clearTimeout(state.timer);
    states.delete(conversationId);
  };

  return {
    // Wrapped so callers can't pass their own `attempt` and start a chain
    // partway through its budget.
    subscribe: (conversationId: string) => subscribe(conversationId),
    cancel,
    cancelAll: () => {
      for (const state of states.values()) {
        if (state.timer) clearTimeout(state.timer);
      }
      states.clear();
    },
  };
}
